/**
 * @file Service API pour les fonctionnalités publiques.
 * @description
 * Ce fichier regroupe un ensemble de services destinés à être utilisés sur les
 * pages publiques de l'application, accessibles par des utilisateurs non
 * authentifiés (les pêcheurs consultant une page de réservation).
 *
 * Il est structuré en sous-services pour une meilleure organisation :
 * - `publicLakeService`: Pour récupérer les informations sur les lacs et postes.
 * - `publicAvailabilityService`: Pour vérifier la disponibilité des postes.
 * - `publicReservationService`: Pour initier le processus de réservation
 *   (qui mènera à une session de paiement).
 * - `publicLegalService`: Pour récupérer le contenu légal sans authentification.
 *
 * L'ensemble est exporté sous `publicApiService` pour un accès unifié.
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { getPublicSessionToken } from "./api.service";
import { PUBLIC } from "../config/api.endpoints";
import type { RentalMode } from "@/utils/rentalMode";
import type {
  SpotAvailability,
  LakeAvailabilityCalendarResponse,
  CalendarDayAvailability,
  PublicReservationOptionResponse,
} from "@/types/api";

// Create a pure public API instance without authentication interceptors
class PurePublicApiService {
  private axios: AxiosInstance;
  // Map to deduplicate concurrent GETs at transport level
  private inflightGet = new Map<string, Promise<unknown>>();

  constructor() {
    this.axios = axios.create({
      baseURL: import.meta.env.VITE_APP_API_URL || "",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": import.meta.env.VITE_API_KEY,
      },
      timeout: 10000,
      withCredentials: true,
    });

    // Request interceptor: attach public session token if available
    this.axios.interceptors.request.use((config) => {
      try {
        const token = sessionStorage.getItem("public_session_token");
        if (token) {
          config.headers = config.headers || {};
          (config.headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
        }
      } catch {
        // Ignore sessionStorage errors (e.g., in private browsing mode)
      }
      return config;
    });

    // Response interceptor to extract data and handle errors with auto-refresh of public token
    this.axios.interceptors.response.use(
      (response: AxiosResponse) => response.data,
      async (error: AxiosError) => {
        const status = error.response?.status;
        type RetryConfig = AxiosRequestConfig & { __publicRetry?: boolean };
        const originalConfig: RetryConfig = (error.config as RetryConfig) || {};
        // The backend signals a missing/expired public session as 401 or 422
        // (dependency validation) depending on the endpoint — not only 403.
        const detail = (error.response?.data as { detail?: unknown } | undefined)
          ?.detail;
        const isSessionError =
          status === 403 ||
          ((status === 401 || status === 422) &&
            typeof detail === "string" &&
            detail.toLowerCase().includes("session publique"));
        if (isSessionError && !originalConfig.__publicRetry) {
          try {
            const newToken = await getPublicSessionToken();
            if (newToken) {
              // Store and retry once
              try {
                sessionStorage.setItem("public_session_token", newToken);
              } catch {
                // Ignore sessionStorage errors (e.g., in private browsing mode)
              }
              originalConfig.__publicRetry = true;
              originalConfig.headers = originalConfig.headers || {};
              (originalConfig.headers as Record<string, string>)["Authorization"] =
                `Bearer ${newToken}`;
              return this.axios.request(originalConfig);
            }
          } catch {
            // Ignore token refresh errors
          }
        }
        console.error("Pure public API error:", error);
        return Promise.reject(error);
      },
    );
  }

  async get<T = unknown>(url: string, params?: Record<string, unknown>): Promise<T> {
    const config: AxiosRequestConfig = params ? { params } : {};
    // Build a stable key for deduplication
    const key = (() => {
      try {
        const qp = params
          ? new URLSearchParams(
              Object.entries(params)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([k, v]) => [k, String(v)]),
            ).toString()
          : "";
        return qp ? `${url}?${qp}` : url;
      } catch {
        return url + "::" + JSON.stringify(params || {});
      }
    })();

    const existing = this.inflightGet.get(key) as Promise<T> | undefined;
    if (existing) return existing;

    const p = this.axios.get(url, config).finally(() => {
      this.inflightGet.delete(key);
    }) as unknown as Promise<T>;

    this.inflightGet.set(key, p);
    return p;
  }

  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.axios.post(url, data, config) as unknown as Promise<T>;
  }
}

// Instance for pure public calls (no auth headers). Exported so other
// services (e.g. pricing.service) can hit public endpoints with the public
// session token instead of the authenticated instance.
export const purePublicApi = new PurePublicApiService();

/**
 * Service API pour les fonctionnalités publiques de réservation
 * Utilisé par les utilisateurs non authentifiés (pêcheurs)
 */

// Types TypeScript pour l'API publique
export interface PublicLakeSummary {
  id: string;
  name: string;
  description?: string;
  location?: {
    address: string;
    city: string;
    postal_code: string;
    latitude?: number | null;
    longitude?: number | null;
  } | null;
  gallery_image_urls?: string[];
  photo_image_url?: string | null;
  lake_plan_image_url?: string | null;
  amenities?: string[];
  regulations?: string | null;
  is_active: boolean;
  // Champ réellement renvoyé par le backend (LakeSummaryResponse.organization_id,
  // requis) mais absent de ce type jusqu'ici : FavoriteButton/LakeReviewsSection
  // le consomment (`:organization-id="lake.organization_id"`), invisible en tsc
  // puisque les .vue ne sont pas vérifiés par `npm run typecheck`.
  organization_id: string;
  organization_slug?: string | null;
  distance_km?: number | null;
  starting_price?: number | null;
  is_new?: boolean;
  is_boosted?: boolean;
  is_popular?: boolean;
  average_rating?: number | null;
  reviews_count?: number | null;
}

export interface PublicCancellationTier {
  jours_avant: number;
  pourcentage: number;
}

export interface PublicCancellationPolicy {
  paliers: PublicCancellationTier[];
  taxe_de_sejour_remboursee: boolean;
}

export interface PublicLakeDetails extends PublicLakeSummary {
  detailed_description?: string;
  /**
   * Types d'hébergement déclarés sur le lac (slugs : `chalet`, `yurt`…).
   * Sert de facette de recherche, et désormais aussi d'information affichée
   * sur la fiche publique.
   *
   * À ne pas confondre avec les hébergements **réservables** (`PublicLodging`),
   * qui ont un prix et une disponibilité.
   */
  accommodation_types?: string[];
  rules?: string[];
  equipment_available?: string[];
  opening_hours?: string;
  contact_info?: {
    phone?: string;
    email?: string;
  };
  cgv_content?: string | null;
  fish_species?: string[];
  total_spots?: number;
}

export interface MarketplaceSearchParams {
  lat?: number;
  lng?: number;
  radius_km?: number;
  fish_species?: string[];
  fishing_types?: string[];
  environments?: string[];
  accommodation_types?: string[];
  general_services?: string[];
  accessibility?: string[];
  q?: string;
  sort_by?: "distance" | "name" | "created_at";
  page?: number;
  per_page?: number;
}

export interface PaginatedLakesResponse {
  items: PublicLakeSummary[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  // compatibility fields from legacy endpoint
  lakes?: PublicLakeSummary[];
  pagination?: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

export interface PublicFishingSpot {
  id: string;
  name: string;
  description?: string;
  lake_id: string;
  capacity: number;

  places: Array<{
    spot_number: number;
    name?: string;
    is_active: boolean;
    is_accessible: boolean;
  }>;
  amenities?: string[];
  /**
   * Ce qui se réserve : une place ou le poste entier. `null`/absent = le
   * gestionnaire ne s'est pas prononcé — passer par `resolveRentalMode`.
   */
  rental_mode?: RentalMode | null;
  pricing: {
    price_per_day: number;
    tarif_type?: string;
    price_per_additional_angler?: number | null;
  };
  // The backend field is photo_image_url (single main photo) — `images`
  // never matched anything the API actually returns and was unused.
  photo_image_url?: string | null;
  is_active: boolean;
}

export interface AvailabilityRequest {
  spot_number?: number;
  start_date: string; // ISO format
  end_date: string; // ISO format
}

export interface AvailabilityResponse {
  fishing_spot_id: string;
  start_date: string;
  end_date: string;
  is_available?: boolean; // Pour une place spécifique
  spot_number?: number; // Si une place spécifique
  available_spots?: number[]; // Places disponibles
  unavailable_spots?: number[]; // Places non disponibles
  conflicting_reservations?: string[];
  all_spots_available?: boolean;
  spots_status?: Record<number, boolean>;
}

// Sourced from the backend's OpenAPI schema (see CLAUDE.md "Fix structurel") —
// these 2 endpoints had no response_model until now, so their shape used to
// be hand-duplicated here with no guarantee it matched what the server
// actually returns. Regenerate via `npm run generate:types` after any change
// to app/schemas/public_availability.py.
export type AggregatedAvailabilityResponse = Record<string, SpotAvailability>;
export type LakeCalendarResponse = LakeAvailabilityCalendarResponse;
export type LakeCalendarDayInfo = CalendarDayAvailability;

export interface RequestedReservationSpot {
  lake_id: string;
  fishing_spot_id: string;
  spot_number: number;
  /** Effectif déclaré, borné par la capacité du poste côté serveur. */
  nombre_pecheurs?: number;
}

export interface RequestedReservationOption {
  option_id: string;
  value: number | string | boolean;
}

// Mirrors the backend's PublicReservationCreate schema (multi-spot).
/** Une unité d'hébergement demandée : c'est elle qui se réserve. */
export interface RequestedReservationLodging {
  lodging_id: string;
  unit_number: number;
  guests: number;
}

export interface PublicReservationRequest {
  reserved_spots: RequestedReservationSpot[];
  options?: RequestedReservationOption[];
  /** Hébergements demandés. Ils partagent les dates du séjour de pêche. */
  reserved_lodgings?: RequestedReservationLodging[];
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  check_in: string; // ISO format
  check_out: string; // ISO format
  notes?: string;
  /**
   * Type de réservation. Seule la journée existe : `price_per_half_day` est
   * désactivé côté backend (commenté dans le modèle ET le schéma) depuis
   * longtemps, mais le tunnel proposait toujours une demi-journée — que le
   * moteur ignorait, et qu'une règle facturait au plein tarif. Le client
   * choisissait une demi-journée et payait la journée.
   */
  booking_type?: "day";
  accepted_cgv: boolean;
  sms_opt_in?: boolean;
  utm_campaign?: string;
  /**
   * Nombre de mineurs déclarés : exonérés de taxe de séjour. Transmis au moteur
   * de prix (le backend le consomme via `PublicReservationCreate.nombre_mineurs`).
   */
  nombre_mineurs?: number;
  /**
   * Code promo saisi par le client. Doit accompagner la réservation : sans lui,
   * le backend recalcule le prix sans la remise que le tunnel a affichée.
   */
  code_promo?: string;
}

/**
 * Forme réellement renvoyée par `POST /public/{org}/lakes/{id}/reservations`
 * (`backend/app/schemas/reservation.py`, `PublicReservationResponse`).
 *
 * L'interface déclarait `payment_session_url` et `total_price`, qui n'existent
 * pas côté backend : le champ est `payment_url`, et le montant `amount` (en
 * centimes). Comme rien ne confronte ce type à l'API, l'écart est resté
 * invisible — et `useReservation` testait un champ toujours `undefined`.
 */
export interface PublicReservationResponse {
  reservation_id: string | null;
  payment_url?: string;
  payment_intent_id: string;
  client_secret: string;
  amount: number;
  currency: string;
  status: string;
}

export interface ReviewResponse {
  id: string;
  rating: number;
  comment?: string | null;
  created_at: string;
  user_id?: string | null;
}

export interface ReviewCreateRequest {
  rating: number;
  comment?: string | null;
}

/**
 * Service pour les opérations publiques sur les lacs
 */
export const publicLakeService = {
  /**
   * Récupérer la liste des lacs publics d'une organisation
   */
  async getOrganizationLakes(
    orgSlug: string,
    options?: {
      page?: number;
      per_page?: number;
      filters?: string;
    }
  ): Promise<PublicLakeSummary[]> {
    try {
      const params = new URLSearchParams();
      if (options?.page && options.page > 1) {
        params.append('page', options.page.toString());
      }
      if (options?.per_page && options.per_page !== 20) {
        params.append('per_page', options.per_page.toString());
      }
      if (options?.filters) {
        params.append('filters', options.filters);
      }

      const queryString = params.toString();
      const url = queryString
        ? `${PUBLIC.LAKES.LIST(orgSlug)}?${queryString}`
        : PUBLIC.LAKES.LIST(orgSlug);

      return purePublicApi.get<PublicLakeSummary[]>(url);
    } catch (error) {
      console.error("Erreur lors de la récupération des lacs publics:", error);
      throw error;
    }
  },

  /**
   * Rechercher les lacs via la marketplace publique
   */
  async searchMarketplace(
    payload: MarketplaceSearchParams,
  ): Promise<PaginatedLakesResponse> {
    const params = new URLSearchParams();

    if (typeof payload.lat === "number") {
      params.append("lat", payload.lat.toString());
    }
    if (typeof payload.lng === "number") {
      params.append("lng", payload.lng.toString());
    }
    if (typeof payload.radius_km === "number") {
      params.append("radius_km", payload.radius_km.toString());
    }
    if (payload.q) {
      params.append("q", payload.q);
    }
    if (payload.sort_by) {
      params.append("sort_by", payload.sort_by);
    }
    if (payload.page && payload.page > 1) {
      params.append("page", payload.page.toString());
    }
    if (payload.per_page) {
      params.append("per_page", payload.per_page.toString());
    }

    const appendArray = (key: string, values?: string[]) => {
      if (!values) return;
      values.forEach((value) => {
        params.append(key, value);
      });
    };

    appendArray("fish_species", payload.fish_species);
    appendArray("fishing_types", payload.fishing_types);
    appendArray("environments", payload.environments);
    appendArray("accommodation_types", payload.accommodation_types);
    appendArray("general_services", payload.general_services);
    appendArray("accessibility", payload.accessibility);

    const url = params.toString()
      ? `${PUBLIC.MARKETPLACE.SEARCH}?${params.toString()}`
      : PUBLIC.MARKETPLACE.SEARCH;

    return purePublicApi.get(url);
  },

  async fetchMarketplaceFacets(params?: {
    lat?: number;
    lng?: number;
    radius_km?: number;
  }): Promise<MarketplaceFacetsResponse> {
    const urlParams = new URLSearchParams()
    if (typeof params?.lat === 'number') {
      urlParams.set('lat', params.lat.toString())
    }
    if (typeof params?.lng === 'number') {
      urlParams.set('lng', params.lng.toString())
    }
    if (typeof params?.radius_km === 'number') {
      urlParams.set('radius_km', params.radius_km.toString())
    }

    const suffix = urlParams.toString()
    const url = suffix ? `${PUBLIC.MARKETPLACE.FACETS}?${suffix}` : PUBLIC.MARKETPLACE.FACETS
    return purePublicApi.get(url)
  },

  /**
   * Récupérer les détails d'un lac public
   */
  async getLakeDetails(
    orgSlug: string,
    lakeId: string,
  ): Promise<PublicLakeDetails> {
    try {
      return purePublicApi.get<PublicLakeDetails>(
        PUBLIC.LAKES.DETAILS(orgSlug, lakeId),
      );
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des détails du lac:",
        error,
      );
      throw error;
    }
  },

  /**
   * Conditions d'annulation d'un lac.
   *
   * `null` quand le gestionnaire n'en a pas posé : le tunnel n'affiche alors
   * rien, plutôt que la phrase « Annulation gratuite jusqu'à 48h » que le
   * produit servait en dur sans que rien ne l'applique.
   */
  async getCancellationPolicy(
    orgSlug: string,
    lakeId: string,
  ): Promise<PublicCancellationPolicy | null> {
    try {
      return await purePublicApi.get<PublicCancellationPolicy | null>(
        PUBLIC.LAKES.CANCELLATION_POLICY(orgSlug, lakeId),
      );
    } catch (error) {
      // Des conditions illisibles ne doivent pas empêcher de réserver.
      console.error(
        "Erreur lors de la récupération des conditions d'annulation:",
        error,
      );
      return null;
    }
  },

  /**
   * Récupérer les postes de pêche d'un lac
   */
  async getLakeFishingSpots(
    orgSlug: string,
    lakeId: string,
  ): Promise<PublicFishingSpot[]> {
    try {
      return purePublicApi.get<PublicFishingSpot[]>(
        PUBLIC.LAKES.FISHING_SPOTS(orgSlug, lakeId),
      );
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des postes de pêche:",
        error,
      );
      throw error;
    }
  },

  async getLakeReviews(
    lakeId: string,
    limit: number = 12,
  ): Promise<ReviewResponse[]> {
    // The reviews router is mounted under /public (api/v1/api.py), not at
    // the API root — these 3 calls 404'd against every lake until fixed.
    return purePublicApi.get<ReviewResponse[]>(`/public/reviews/${lakeId}`, {
      params: { limit },
    });
  },

  async createLakeReview(
    lakeId: string,
    payload: ReviewCreateRequest,
    organizationId: string,
  ): Promise<ReviewResponse> {
    return purePublicApi.post<ReviewResponse>(
      `/public/reviews/${lakeId}`,
      payload,
      {
        params: { organization_id: organizationId },
      },
    );
  },

  async submitVerifiedReview(
    payload: ReviewCreateRequest,
    token: string,
  ): Promise<ReviewResponse> {
    return purePublicApi.post<ReviewResponse>(
      '/public/reviews/verified/submit',
      payload,
      {
        params: { token },
      },
    );
  },
};

/**
 * Service pour vérifier les disponibilités
 */
export const publicAvailabilityService = {
  /**
   * Vérifier la disponibilité d'un poste de pêche
   */
  async checkAvailability(
    orgSlug: string,
    lakeId: string,
    spotId: string,
    request: AvailabilityRequest,
  ): Promise<AvailabilityResponse> {
    try {
      const params = {
        start_date: request.start_date,
        end_date: request.end_date,
        ...(request.spot_number && { spot_number: request.spot_number }),
      };

      return purePublicApi.get<AvailabilityResponse>(
        PUBLIC.AVAILABILITY.CHECK(orgSlug, lakeId, spotId),
        params,
      );
    } catch (error) {
      console.error("Erreur lors de la vérification de disponibilité:", error);
      throw error;
    }
  },

  async getAggregatedAvailability(
    orgSlug: string,
    lakeId: string,
    startDate: string,
    endDate: string,
  ): Promise<AggregatedAvailabilityResponse> {
    // Backend expects plain YYYY-MM-DD dates — truncate defensively in case a
    // caller still passes a full ISO datetime.
    return purePublicApi.get<AggregatedAvailabilityResponse>(
      PUBLIC.AVAILABILITY.AGGREGATED(orgSlug, lakeId),
      {
        start_date: startDate.slice(0, 10),
        end_date: endDate.slice(0, 10),
      },
    );
  },

  /**
   * Agrégat de disponibilité par jour pour UN mois (pastilles du calendrier).
   * Le backend attend {month, year} — l'ancien appel envoyait
   * {spot_ids, start_date, end_date} et attendait {days, matrix}, un contrat
   * qui n'a jamais existé côté serveur.
   */
  async getLakeCalendar(
    orgSlug: string,
    lakeId: string,
    month: number,
    year: number,
  ): Promise<LakeCalendarResponse> {
    const url = PUBLIC.AVAILABILITY.CALENDAR(orgSlug, lakeId);
    // Must go through the public instance: anonymous visitors have no user
    // auth, only the public session token this instance attaches.
    return purePublicApi.post<LakeCalendarResponse>(url, { month, year });
  },
};

/**
 * Service pour les réservations publiques
 */
export const publicReservationService = {
  /**
   * Créer une réservation publique
   */
  async createReservation(
    orgSlug: string,
    lakeId: string,
    reservation: PublicReservationRequest,
    successUrl: string,
    cancelUrl: string,
  ): Promise<PublicReservationResponse> {
    try {
      const url = PUBLIC.RESERVATIONS.CREATE(orgSlug, lakeId);
      const params = {
        success_url: successUrl,
        cancel_url: cancelUrl,
      };

      // Instance publique obligatoire: un visiteur anonyme n'a que le jeton
      // de session publique, que l'instance authentifiée n'attache pas.
      return purePublicApi.post<PublicReservationResponse>(url, reservation, {
        params,
      });
    } catch (error) {
      console.error("Erreur lors de la création de la réservation:", error);
      throw error;
    }
  },

  /**
   * Résout un `session_id` Stripe Checkout en `reservation_id`/`email`.
   *
   * Le tunnel payant redirige avec `?session_id=...` avant que la
   * réservation n'existe (créée par le webhook, après la redirection) :
   * `reservation_id` n'est donc jamais présent sur cette URL. Peut renvoyer
   * 404 si le webhook n'a pas encore traité l'événement — l'appelant doit
   * réessayer avec un court backoff avant d'abandonner.
   */
  async resolveCheckoutSession(
    orgSlug: string,
    sessionId: string,
  ): Promise<{ reservation_id: string; email: string }> {
    return purePublicApi.get<{ reservation_id: string; email: string }>(
      PUBLIC.RESERVATIONS.RESOLVE_SESSION(orgSlug),
      { params: { session_id: sessionId } },
    );
  },
};

export interface PublicOrganizationConfig {
  name: string;
  theme?: {
    primary_color?: string;
    logo_url?: string;
  };
  contact?: {
    email?: string | null;
    phone?: string | null;
  };
  features: {
    sms_enabled?: boolean;
    sms_consent_available?: boolean;
  };
}

export const publicConfigService = {
  async getOrganizationConfig(orgSlug: string): Promise<PublicOrganizationConfig> {
    return purePublicApi.get<PublicOrganizationConfig>(PUBLIC.CONFIG.ORGANIZATION(orgSlug));
  },
};

/**
 * Service pour le contenu légal public (sans authentification)
 */
export const publicLegalService = {
  /**
   * Récupérer le contenu des CGV d'une organisation
   */
  async getCgvContent(orgSlug: string): Promise<{ content: string }> {
    try {
      return purePublicApi.get<{ content: string }>(PUBLIC.LEGAL.CGV(orgSlug));
    } catch (error) {
      console.error("Erreur lors de la récupération des CGV:", error);
      throw error;
    }
  },

  /**
   * Récupérer le contenu des mentions légales d'une organisation
   */
  async getLegalMentionsContent(orgSlug: string): Promise<{ content: string }> {
    try {
      return purePublicApi.get<{ content: string }>(PUBLIC.LEGAL.MENTIONS(orgSlug));
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des mentions légales:",
        error,
      );
      throw error;
    }
  },
};

/**
 * Service pour les options de réservation publiques
 */
export const publicReservationOptionsService = {
  /**
   * Liste les options actives d'un lac.
   *
   * Les dates sont optionnelles mais indissociables : fournies, chaque option
   * porte en plus `remaining_quantity` (exemplaires encore disponibles sur ce
   * créneau). Sans dates, le backend renvoie `remaining_quantity: null`.
   */
  async listForLake(
    orgSlug: string,
    lakeId: string,
    checkIn?: string | null,
    checkOut?: string | null,
  ): Promise<PublicReservationOptionResponse[]> {
    const params = checkIn && checkOut ? { check_in: checkIn, check_out: checkOut } : undefined;
    return purePublicApi.get<PublicReservationOptionResponse[]>(
      PUBLIC.LAKES.RESERVATION_OPTIONS(orgSlug, lakeId),
      params,
    );
  },
};

/** Une unité d'hébergement encore libre sur le créneau demandé. */
export interface PublicLodgingUnit {
  unit_number: number;
  name: string | null;
}

/** Un hébergement tel que présenté dans le tunnel de réservation. */
export interface PublicLodging {
  id: string;
  name: string;
  description: string | null;
  lodging_type: string | null;
  /** Nombre de personnes pouvant dormir dans une unité. */
  sleeps: number;
  price_per_night: number;
  price_type: "par_unite" | "par_personne";
  min_nights: number | null;
  amenities: string[];
  photo_image_url: string | null;
  gallery_image_urls: string[];
  total_units: number;
  /** `null` tant qu'aucune date n'est fournie : le restant est inconnu. */
  available_units: PublicLodgingUnit[] | null;
  remaining_units: number | null;
}

/**
 * Service pour les hébergements réservables (chalet, cabane, mobil-home).
 */
export const publicLodgingService = {
  /**
   * Liste les hébergements actifs d'un lac.
   *
   * Les dates sont optionnelles mais indissociables : fournies, chaque
   * hébergement porte `available_units` (les unités encore libres, nommées) et
   * `remaining_units`. Sans dates, ces deux champs valent `null` — on ne peut
   * pas connaître le restant sans créneau.
   */
  async listForLake(
    orgSlug: string,
    lakeId: string,
    checkIn?: string | null,
    checkOut?: string | null,
  ): Promise<PublicLodging[]> {
    const params =
      checkIn && checkOut ? { check_in: checkIn, check_out: checkOut } : undefined;
    return purePublicApi.get(PUBLIC.LAKES.LODGINGS(orgSlug, lakeId), params);
  },
};

/**
 * Service API public complet
 */
export const publicApiService = {
  lakes: publicLakeService,
  availability: publicAvailabilityService,
  reservations: publicReservationService,
  legal: publicLegalService,
  options: publicReservationOptionsService,
  lodgings: publicLodgingService,
  config: publicConfigService,
};

export default publicApiService;

export interface FacetBucket {
  slug: string;
  count: number;
}

export interface MarketplaceFacetsResponse {
  fish_species: FacetBucket[];
  fishing_types: FacetBucket[];
  environments: FacetBucket[];
  accommodation_types: FacetBucket[];
  general_services: FacetBucket[];
  accessibility: FacetBucket[];
}
